import { Icon, Skeleton, Tooltip, TooltipProps } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import { PropsWithChildren, PropsWithClassName } from 'lib-shared-types-utils';
import { useMemo } from 'react';

import { useQueueDashboardQueueContext } from '../queue/queueDashboardQueue.context';

import {
  QueueDashboardMetricTitle,
  QueueDashboardMetricTitleProps,
} from './components/title/queueDashboardMetricTitle';
import styles from './queueDashboardMetric.module.scss';

export type QueueDashboardMetricProps = {
  /** The props for the tooltip when hovering the alert icon. */
  alertTooltip?: Pick<TooltipProps, 'content' | 'head'> | null;
  /** Whether to show the skeleton UI, e.g. when loading. */
  isSkeleton?: boolean;
} & Pick<QueueDashboardMetricTitleProps, 'metricType' | 'title'> &
  PropsWithChildren &
  PropsWithClassName;

export const QueueDashboardMetric = ({
  className,
  children,
  metricType,
  title,
  alertTooltip,
  isSkeleton,
}: QueueDashboardMetricProps): JSX.Element => {
  const { layout } = useQueueDashboardQueueContext();
  const showTitle = layout !== 'compact';

  const alertTooltipElement = useMemo(
    () =>
      alertTooltip &&
      !isSkeleton && (
        <Tooltip
          {...alertTooltip}
          className={styles.tooltip}
          wrap="div"
          wrapProps={{
            className: classNames(styles.tooltipWrapper, {
              [styles.floating]: !showTitle,
            }),
          }}
        >
          <Icon
            name="exclamation-circle-filled"
            color={typography.color.error}
          />
        </Tooltip>
      ),
    [alertTooltip, isSkeleton, showTitle],
  );

  return (
    <div className={classNames(styles.root, className)}>
      {showTitle ? (
        <div className={styles.title}>
          {isSkeleton ? (
            <Skeleton height="16px" width="100px" />
          ) : (
            <QueueDashboardMetricTitle metricType={metricType} title={title} />
          )}

          {alertTooltipElement}
        </div>
      ) : (
        alertTooltipElement
      )}

      {children}
    </div>
  );
};
